// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

/**
 * Type-safe helper for accessing i18n translation keys dynamically.
 *
 * Use when the key is a runtime string (e.g., from API data or template literals)
 * instead of casting the translation object with `as any`.
 *
 * @example
 *   tKey(t.license, license.status)          // dynamic property lookup
 *   tKey(t.assessments, `risk_level_${val}`) // template literal key
 */
export function tKey(
  obj: Record<string, unknown> | undefined | null,
  key: string,
  fallback?: string,
): string {
  if (!obj || typeof obj !== 'object') return fallback ?? key;
  const value = (obj as Record<string, unknown>)[key];
  if (typeof value === 'string') return value;
  return fallback ?? key;
}

/**
 * Type-safe accessor for nested translation objects.
 *
 * @example
 *   const labels = tNested(t.admin, 'org_maturity');
 *   labels.title // string
 */
export function tNested(
  obj: Record<string, unknown> | undefined | null,
  key: string,
): Record<string, string> {
  if (!obj || typeof obj !== 'object') return {};
  const value = (obj as Record<string, unknown>)[key];
  if (value && typeof value === 'object') return value as Record<string, string>;
  return {};
}

/**
 * Formats an enum/snake_case value into a human-readable label.
 * Replaces underscores with spaces and capitalizes the first letter of each word.
 *
 * @example
 *   formatEnumLabel('data_breach')       // 'Data Breach'
 *   formatEnumLabel('in_progress')       // 'In Progress'
 *   formatEnumLabel(undefined)           // '-'
 */
export function formatEnumLabel(value: string | undefined | null, fallback = '-'): string {
  if (!value) return fallback;
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Pick the locale variant of a `{ en, vi, ... }` content record.
 *
 * Used for *dynamic* per-record content (regulatory titles, framework
 * names, form labels stored as `{ [locale]: string }` maps on the row).
 * For static UI labels use the `t.*` translation table instead.
 *
 * Resolution order:
 *   1. Exact locale (`raw[locale]`)
 *   2. Bare language (`raw[locale.split('-')[0]]`) — e.g. `en` for `en-US`
 *   3. `raw.en` (final fallback so the UI never shows an empty cell)
 *
 * In dev, warns once per (locale, available-keys) tuple when no exact
 * match is found, so missing translations surface during development.
 * Returns `''` if every variant is empty so consumers can `if (label) …`.
 */
const _localisedFieldWarned = new Set<string>();
export function localisedFieldFromMap(
  raw: Record<string, string | null | undefined> | null | undefined,
  locale: string,
): string {
  if (!raw) return '';
  const lang = locale.split('-')[0];
  const direct = raw[locale];
  if (typeof direct === 'string' && direct.length > 0) return direct;
  const bare = raw[lang];
  if (typeof bare === 'string' && bare.length > 0) {
    return bare;
  }
  const en = raw.en;
  if (typeof en === 'string' && en.length > 0) {
    if (
      process.env.NODE_ENV !== 'production' &&
      lang !== 'en' &&
      locale !== 'en' &&
      !direct &&
      !bare
    ) {
      const key = `${locale}|${Object.keys(raw).sort().join(',')}`;
      if (!_localisedFieldWarned.has(key)) {
        _localisedFieldWarned.add(key);
        console.warn(
          `[localisedFieldFromMap] no ${locale} translation; falling back to en. keys=${Object.keys(raw).join(',')}`,
        );
      }
    }
    return en;
  }
  return '';
}

/**
 * Resolve a localised form/template name. The API may return:
 *   - a flat string (already localised on the server)
 *   - a `{ [locale]: string }` map keyed by exact locale, bare lang,
 *     and/or `en` / `vi` fallback variants
 *
 * Used in template-picker callbacks (AuthorityRequestsPage,
 * BreachNotificationsPage). Centralised so the resolution rules cannot
 * drift between call sites.
 */
export function resolveLocalisedFormName(
  raw: string | Record<string, string | undefined | null> | null | undefined,
  locale: string,
): string {
  if (typeof raw === 'string') return raw;
  if (!raw) return '';
  return localisedFieldFromMap(raw as Record<string, string | null | undefined>, locale);
}

/**
 * Pick the locale variant from a pair of `(en, vi)` strings stored as
 * sibling fields on a record (`title` + `title_vi`, `description` +
 * `description_vi`, …). Used widely in Forseti workflow + conflict
 * adapters where the API returns the EN value on the base field and
 * the VI value on the `_vi` companion.
 *
 * For locales that are not `vi`, returns the EN value. To support more
 * locales, migrate the storage shape to `{ en, vi, da, … }` and use
 * `localisedFieldFromMap` instead.
 */
export function localisedField(
  en: string | null | undefined,
  vi: string | null | undefined,
  locale: string,
): string {
  if (locale === 'vi' && typeof vi === 'string' && vi.length > 0) return vi;
  return typeof en === 'string' ? en : '';
}
